<template>
  <div class="d-flex flex-column justify-content-start" style="height: 100%">
    <div class="flex-grow-1" style="height: 100%">
      <x-card home class="d-flex flex-column flex" style="height: 100%">
        <v-card-title>
          <div class="d-md-flex d-sm-block" style="width: 100%">
            <h4 class="d-sm-none d-md-block mr-4">Result Uploads</h4>
            <div class="d-md-flex d-sm-block ml-auto justify-end flex-wrap" style="gap: 8px">
              <x-text-field dense outlined hide-details
                            placeholder="Course code (e.g. CSC-101)"
                            background-color="white"
                            v-model="filterCourseCode"
                            style="max-width: 220px">
              </x-text-field>
              <x-text-field dense outlined hide-details
                            placeholder="Session (e.g. 2024/2025)"
                            background-color="white"
                            v-model="filterSession"
                            style="max-width: 200px">
              </x-text-field>
              <x-button color="primary" outlined small
                        :disabled="!filterCourseCode || !filterSession || loading"
                        @click="applyCourseFilter">
                Apply
              </x-button>
              <x-button color="default" outlined small
                        :disabled="!isFiltered || loading"
                        @click="resetFilter">
                Reset
              </x-button>
              <x-text-field dense outlined hide-details
                            placeholder="Search path/name"
                            append-icon="mdi-magnify"
                            background-color="white"
                            v-model="search"
                            clearable
                            style="max-width: 240px">
              </x-text-field>
              <x-button icon outlined small @click="reload" :loading="loading" title="Refresh">
                <v-icon>mdi-refresh</v-icon>
              </x-button>
            </div>
          </div>
          <div v-if="isFiltered" class="caption grey--text mt-2">
            Filtered by course <strong>{{ filterCourseCode }}</strong> / session
            <strong>{{ filterSession }}</strong> &mdash; substring match, may include similar codes.
          </div>
        </v-card-title>
        <v-divider />
        <x-card-text>
          <v-data-table :headers="headers" :items="filteredFiles" item-key="path"
                        class="my-5 mx-2"
                        :loading="loading"
                        :search="search"
                        :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
                        :items-per-page.sync="itemsPerPage"
                        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
                        no-data-text="No result files found">
            <template v-slot:[`item.name`]="{ item }">
              <span class="font-weight-medium">{{ item.name }}</span>
            </template>
            <template v-slot:[`item.path`]="{ item }">
              <span class="text-caption" style="font-family: monospace">{{ item.path }}</span>
            </template>
            <template v-slot:[`item.size`]="{ item }">
              {{ formatBytes(item.size) }}
            </template>
            <template v-slot:[`item.lastModified`]="{ item }">
              {{ formatTimestamp(item.lastModified) }}
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-menu bottom left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn dark icon v-bind="attrs" v-on="on">
                    <v-icon color="black">mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item link @click="downloadFile(item)">
                    <v-list-item-title>Download</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="canDelete" link @click="confirmDelete(item)">
                    <v-list-item-title class="error--text">Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </x-card-text>
      </x-card>
    </div>

    <v-dialog v-model="showDeleteDialog" max-width="460" persistent>
      <v-card v-if="fileToDelete">
        <v-card-title class="text-h6">Delete result file</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          Permanently delete <strong>{{ fileToDelete.name }}</strong>?
          <div class="text-caption mt-2" style="font-family: monospace; word-break: break-all">
            {{ fileToDelete.path }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDeleteDialog" :disabled="deleting">Cancel</v-btn>
          <v-btn color="error" :loading="deleting" @click="executeDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <loading-dialog :show="downloading"></loading-dialog>
    <message-box :show="messageShow" :message="message" :type="messageType"
                 v-on:close-message="messageShow = !messageShow"></message-box>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Api from "@/main/api";
import Roles from "@/shared/Roles";
import ErrorResolver from "@/shared/ErrorResolver";
import LoadingDialog from "@ui/components/LoadingDialog";
import MessageBox from "@ui/components/MessageBox";
import XButton from "@ui/widgets/XButton.vue";
import XCard from "@ui/components/XCard.vue";
import XCardText from "@ui/components/XCardText.vue";
import XTextField from "@ui/widgets/XTextField.vue";

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

export default {
  name: "ResultAuditPage",
  components: { LoadingDialog, MessageBox, XButton, XCard, XCardText, XTextField },

  data: () => ({
    files: [],
    loading: false,
    search: '',
    filterCourseCode: '',
    filterSession: '',
    isFiltered: false,

    sortBy: 'lastModified',
    sortDesc: true,
    itemsPerPage: 25,

    showDeleteDialog: false,
    fileToDelete: null,
    deleting: false,

    downloading: false,

    messageType: '',
    message: '',
    messageShow: false,

    headers: [
      { text: 'Name', value: 'name' },
      { text: 'Path', value: 'path' },
      { text: 'Size', value: 'size', align: 'end' },
      { text: 'Last modified', value: 'lastModified' },
      { text: 'Actions', value: 'actions', sortable: false, align: 'end' },
    ],
  }),

  computed: {
    ...mapGetters(['getUser']),

    /**
     * Backend allows ADMIN or RESULT_ADMIN to delete. SUPER_ADMIN inherits
     * ADMIN, so it lands in that set too.
     */
    canDelete() {
      const user = this.getUser;
      const roles = (user && (user.roles || (user.user && user.user.roles))) || [];
      return Roles.deepContains([Roles.Admin, Roles.SuperAdmin, Roles.ResultAdmin], roles);
    },

    filteredFiles() {
      return this.files;
    },
  },

  methods: {
    reload() {
      if (this.isFiltered) {
        this.applyCourseFilter();
      } else {
        this.fetchAll();
      }
    },

    fetchAll() {
      this.loading = true;
      Api.resultAudit.listAll()
        .then(res => { this.files = Array.isArray(res.data) ? res.data : []; })
        .catch(err => ErrorResolver.checkErrors(this, err))
        .finally(() => this.loading = false);
    },

    applyCourseFilter() {
      const code = (this.filterCourseCode || '').trim();
      const session = (this.filterSession || '').trim();
      if (!code || !session) return;
      this.loading = true;
      Api.resultAudit.listByCourse(code, session)
        .then(res => {
          this.files = Array.isArray(res.data) ? res.data : [];
          this.isFiltered = true;
        })
        .catch(err => ErrorResolver.checkErrors(this, err))
        .finally(() => this.loading = false);
    },

    resetFilter() {
      this.filterCourseCode = '';
      this.filterSession = '';
      this.isFiltered = false;
      this.fetchAll();
    },

    confirmDelete(item) {
      this.fileToDelete = item;
      this.showDeleteDialog = true;
    },

    closeDeleteDialog() {
      this.showDeleteDialog = false;
      this.fileToDelete = null;
    },

    executeDelete() {
      const item = this.fileToDelete;
      if (!item) return;
      this.deleting = true;
      Api.resultAudit.deleteFile(item.path)
        .then(() => {
          this.message = 'File deleted';
          this.messageType = 'success';
          this.messageShow = true;
          this.closeDeleteDialog();
          this.reload();
        })
        .catch(err => ErrorResolver.checkErrors(this, err))
        .finally(() => this.deleting = false);
    },

    downloadFile(item) {
      this.downloading = true;
      Api.resultAudit.download(item.path)
        .then(res => this.triggerBlobDownload(res.data, item.name))
        .catch(err => ErrorResolver.checkErrors(this, err))
        .finally(() => this.downloading = false);
    },

    triggerBlobDownload(blob, filename) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    },

    formatBytes(bytes) {
      if (bytes === null || bytes === undefined) return '';
      let value = Number(bytes);
      if (!Number.isFinite(value)) return '';
      let unit = 0;
      while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
        value /= 1024;
        unit += 1;
      }
      const formatted = unit === 0 ? value.toFixed(0) : value.toFixed(1);
      return `${formatted} ${BYTE_UNITS[unit]}`;
    },

    formatTimestamp(ms) {
      if (!ms) return '';
      const d = new Date(Number(ms));
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleString();
    }
  },

  created() {
    this.fetchAll();
  }
}
</script>

<style scoped>
.v-btn {
  text-transform: none;
}
</style>
